import { useCallback, useMemo, useState } from 'react';

import { usePrevious } from './use-utils.hooks';

/**
 * Options for {@link useNumberState} to clamp values and block sync.
 */
export type NumberStateOptions = {
  /** Inclusive clamp range applied on init and updates. */
  clamp?: [min: number, max: number];
  /** Prevents external value from syncing while true. */
  isSyncBlocked?: boolean;
};

/**
 * Keeps a numeric state that clamps updates and optionally mirrors a changing
 * external value. When `isSyncBlocked` is true, external changes are ignored
 * until unblocked, while manual updates still clamp.
 *
 * @param value External value used to initialize and sync by default. Set `options.isSyncBlocked` to true to opt out of syncing.
 * @param options See {@link NumberStateOptions}.
 * @returns State value and a setter that applies clamping.
 */
export const useNumberState = (
  value: number,
  options?: NumberStateOptions,
): [number, (value: number) => void] => {
  const clamp = useMemo(() => {
    if (!options?.clamp) return (n: number) => n;
    const [a, b] = options.clamp;
    const [min, max] = a < b ? [a, b] : [b, a];
    return (nextValue: number) => Math.max(min, Math.min(nextValue, max));
  }, [options?.clamp]);

  const [state, _setState] = useState(() => clamp(value));
  const isSyncBlocked = options?.isSyncBlocked ?? false;
  const previousValue = usePrevious(value, { setInitial: true });

  const setState = useCallback(
    (nextValue: number) => _setState(clamp(nextValue)),
    [_setState, clamp],
  );

  if (!isSyncBlocked && previousValue !== value) setState(value);

  return [state, setState];
};
